import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { PageHero } from "@/components/site/PageHero";
import { faqCategories, faqs } from "@/data/faq";
import faqMann from "@/assets/schober/faq-mann-telefon.webp";
import ctaBg from "@/assets/schober/cta-faq-bg.webp";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ – Häufige Fragen zu Umzug, Entrümpelung & Transport | Schober" },
      {
        name: "description",
        content:
          "Antworten auf 25+ häufige Fragen: Preise, Einsatzgebiet, Termine, Messie-Räumung, Versicherung, Demontage, Wohnungsauflösung. Schober Transportservice Wien & NÖ.",
      },
      { property: "og:title", content: "FAQ – Schober Transportservice" },
      {
        property: "og:description",
        content: "25+ Antworten zu Umzug, Entrümpelung, Räumung, Transport, Preisen und Versicherung.",
      },
      { property: "og:url", content: "https://schober-transportservice.at/faq" },
    ],
    links: [{ rel: "canonical", href: "https://schober-transportservice.at/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  return (
    <>
      <PageHero title="FAQ" subtitle="Sie haben Fragen?" />

      <section className="container mx-auto max-w-7xl grid gap-10 px-4 py-20 lg:grid-cols-[1.7fr_1fr] lg:items-start">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-brand-orange md:text-4xl">
            Häufig gestellte Fragen
          </h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Die wichtigsten Antworten rund um Umzug, Entrümpelung, Wohnungsauflösung,
            Messie-Räumung und Transport in Wien, Niederösterreich und Burgenland.
            Sie finden Ihre Antwort nicht?{" "}
            <Link to="/kontakt" className="font-semibold text-brand-orange hover:underline">
              Schreiben Sie uns
            </Link>
            .
          </p>

          {faqCategories.map((cat, ci) => (
            <div key={cat.category} className="mt-12 first:mt-10">
              <h2 className="font-display text-xl font-bold text-brand-navy md:text-2xl">
                {cat.category}
              </h2>
              <AccordionPrimitive.Root
                type="single"
                collapsible
                className="mt-4"
              >
                {cat.items.map((f, i) => (
                  <AccordionPrimitive.Item
                    key={i}
                    value={`item-${ci}-${i}`}
                    className="border-b border-border"
                  >
                    <AccordionPrimitive.Header className="flex">
                      <AccordionPrimitive.Trigger className="group flex flex-1 items-center gap-4 py-3 text-left font-semibold text-brand-navy transition-colors hover:text-brand-orange [&[data-state=open]>span>svg]:rotate-45">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-white bg-black">
                          <Plus className="h-5 w-5 transition-transform duration-200" />
                        </span>
                        <span>{f.q}</span>
                      </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>
                    <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                      <div className="pb-5 pl-10 pr-2 leading-relaxed text-muted-foreground">
                        {f.a}
                      </div>
                    </AccordionPrimitive.Content>
                  </AccordionPrimitive.Item>
                ))}
              </AccordionPrimitive.Root>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-0 shadow-xl lg:sticky lg:top-24">
          <img
            src={faqMann}
            alt="Mitarbeiter am Telefon – Schober Transportservice Kundenberatung"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </div>
      </section>

      <section
        className="relative bg-cover bg-center py-20 md:py-40"
        style={{ backgroundImage: `url(${ctaBg})` }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h2 className="mx-auto max-w-3xl font-display text-3xl font-extrabold text-brand-orange-on-dark md:text-4xl">
            Haben Sie weitere Fragen?
          </h2>
          <p className="mx-auto mt-6 max-w-2xl leading-relaxed text-white">
            Wenn Sie Ihre Antwort hier nicht gefunden haben, rufen Sie uns einfach
            an oder schreiben Sie. Persönliche Beratung – ohne Verkaufsdruck.
          </p>
          <Link
            to="/kontakt"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-orange px-10 py-3.5 text-sm font-bold uppercase tracking-wider text-brand-orange-foreground shadow-lg transition-all hover:scale-105"
          >
            Kontakt aufnehmen
          </Link>
        </div>
      </section>
    </>
  );
}
